import { safeLocalStorage } from "../utils/safeLocalStorage";

const STORAGE_KEY_PREFIX = "GlobalSearchBox.recentSearches";
const MAX_RECENT_SEARCHES = 10;

export interface GlobalSearchOption {
  id: string;
  resource: "students" | "instructors" | "drivingSchools" | "unknown";
  name: string;
  group: "Fahrschüler" | "Fahrlehrer" | "Fahrschulen" | "Gesetzliche Vertreter" | "Unbekannt";
  subTextDetails?: {
    text: string;
    relatedResource?: string;
  };
}

export function getRecentSearches(userId: string): GlobalSearchOption[] {
  const storedValue = safeLocalStorage.getItem<unknown>(storageKey(userId), JSON.parse);
  if (!Array.isArray(storedValue)) return [];
  return storedValue.filter(isGlobalSearchOption).slice(0, MAX_RECENT_SEARCHES);
}

export function addRecentSearch(userId: string, option: GlobalSearchOption): GlobalSearchOption[] {
  const recentSearches = [
    option,
    ...getRecentSearches(userId).filter((recentSearch) => !isSameSearchOption(recentSearch, option)),
  ].slice(0, MAX_RECENT_SEARCHES);
  safeLocalStorage.setItem(storageKey(userId), recentSearches, JSON.stringify);
  return recentSearches;
}

export function removeRecentSearch(userId: string, option: GlobalSearchOption): GlobalSearchOption[] {
  const recentSearches = getRecentSearches(userId).filter((recentSearch) => !isSameSearchOption(recentSearch, option));
  if (recentSearches.length === 0) {
    clearRecentSearches(userId);
  } else {
    safeLocalStorage.setItem(storageKey(userId), recentSearches, JSON.stringify);
  }
  return recentSearches;
}

export function clearRecentSearches(userId: string): void {
  safeLocalStorage.removeItem(storageKey(userId));
}

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}.${userId}`;
}

function isSameSearchOption(first: GlobalSearchOption, second: GlobalSearchOption): boolean {
  return first.resource === second.resource && first.id === second.id;
}

function isGlobalSearchOption(value: unknown): value is GlobalSearchOption {
  if (!value || typeof value !== "object") return false;
  const option = value as Partial<GlobalSearchOption>;
  if (
    typeof option.id !== "string" ||
    typeof option.name !== "string" ||
    !isGlobalSearchResource(option.resource) ||
    !isGlobalSearchGroup(option.group)
  ) {
    return false;
  }
  if (!option.subTextDetails) return true;
  return (
    typeof option.subTextDetails === "object" &&
    typeof option.subTextDetails.text === "string" &&
    (option.subTextDetails.relatedResource === undefined || typeof option.subTextDetails.relatedResource === "string")
  );
}

function isGlobalSearchResource(value: unknown): value is GlobalSearchOption["resource"] {
  return value === "students" || value === "instructors" || value === "drivingSchools" || value === "unknown";
}

function isGlobalSearchGroup(value: unknown): value is GlobalSearchOption["group"] {
  return (
    value === "Fahrschüler" ||
    value === "Fahrlehrer" ||
    value === "Fahrschulen" ||
    value === "Gesetzliche Vertreter" ||
    value === "Unbekannt"
  );
}
